import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadSimple, X, Lightbulb, ArrowLeft, FilePdf, FileDoc, Sparkle } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { libraryImages } from '../assets/imageLibrary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Each template's `id` matches the backend FREE_TEMPLATES key in /app/backend/server.py
// Images come exclusively from the central Assets Library — no external CDN,
// no stock photography.
const templates = [
  {
    id: 'diagnosis',
    category: 'quality',
    categoryLabel: 'قوالب الجودة',
    title: 'التقييم الذاتي الشامل',
    titleEn: 'Comprehensive Self-Assessment',
    description:
      'أداة مهنية تساعد منشآت الطفولة المبكرة على إجراء تقييم ذاتي شامل لواقعها الحالي وفق محاور ومعايير منظمة، بما يتيح لها التعرف على مستوى التطبيق وتوثيق النتائج بصورة واضحة.',
    image: libraryImages.documentationPlants,
    recommendBefore: null,
  },
  {
    id: 'analysis',
    category: 'quality',
    categoryLabel: 'قوالب الجودة',
    title: 'نموذج التحليل',
    titleEn: 'Analysis Template',
    description: 'نموذج يساعد على قراءة البيانات وتحويلها إلى مؤشرات واضحة وتوصيات عملية',
    image: libraryImages.documentationPlantsAr,
    recommendBefore: ['diagnosis'],
  },
  {
    id: 'improvement',
    category: 'operational',
    categoryLabel: 'قوالب التشغيل',
    title: 'نموذج خطة التحسين',
    titleEn: 'Improvement Plan Template',
    description: 'نموذج متكامل لبناء خطة تحسين قابلة للتنفيذ وفق أولويات واضحة',
    image: libraryImages.artColorExploration,
    recommendBefore: ['diagnosis', 'analysis'],
  },
  {
    id: 'monitoring',
    category: 'operational',
    categoryLabel: 'قوالب التشغيل',
    title: 'نموذج المتابعة وقياس الأداء',
    titleEn: 'Performance Tracking Template',
    description: 'أداة لمتابعة التنفيذ وقياس التقدم بشكل دوري ومنظم',
    image: libraryImages.inquiryNatureCorner,
    recommendBefore: ['improvement'],
  },
  {
    id: 'accreditation',
    category: 'quality',
    categoryLabel: 'قوالب الجودة',
    title: 'نموذج الاستعداد للاعتماد',
    titleEn: 'Accreditation Readiness Template',
    description: 'نموذج يساعد على تنظيم الجاهزية وتهيئة الملفات والمتطلبات الأساسية',
    image: libraryImages.classAgreements,
    recommendBefore: ['diagnosis', 'improvement'],
  },
];

const CATEGORIES = [
  { id: 'all', label: 'جميع الموارد' },
  { id: 'administrative', label: 'قوالب إدارية' },
  { id: 'educational', label: 'قوالب تربوية' },
  { id: 'quality', label: 'قوالب الجودة' },
  { id: 'operational', label: 'قوالب التشغيل' },
  { id: 'hr', label: 'قوالب الموارد البشرية' },
  { id: 'checklists', label: 'قوائم مرجعية' },
];

const recommendationMessages = {
  analysis: {
    title: 'قبل تحميل نموذج التحليل',
    message:
      'لتحقيق أفضل استفادة من نموذج التحليل، نوصي أولاً باستخدام نموذج التقييم الذاتي الشامل لجمع البيانات الأساسية التي ستحتاجها للتحليل.',
  },
  improvement: {
    title: 'قبل تحميل نموذج خطة التحسين',
    message:
      'لبناء خطة تحسين فعّالة ودقيقة، نوصي أولاً بإكمال التقييم الذاتي الشامل ثم مرحلة التحليل لتحديد الواقع الحالي والفجوات بدقة.',
  },
  monitoring: {
    title: 'قبل تحميل نموذج المتابعة',
    message:
      'لتتمكن من متابعة الأداء بفعالية، نوصي أولاً ببناء خطة تحسين واضحة تحدد الأهداف والمؤشرات المراد قياسها.',
  },
  accreditation: {
    title: 'قبل تحميل نموذج الاستعداد للاعتماد',
    message:
      'للاستعداد الأمثل للاعتماد، نوصي بإكمال التقييم الذاتي الشامل وبناء خطة تحسين شاملة أولاً.',
  },
};

const SIMPLIFIED_NOTE =
  'هذا نموذج مبسط يساعدك على القراءة الأولية فقط، ولا يغني عن الأداة الكاملة التي توفر تقييمًا أعمق وأكثر ارتباطًا بواقع المنشأة.';

const FULL_TOOL_CTA =
  'للحصول على تقييم ذاتي شامل ومنظم وفق محاور ومعايير احترافية، انتقل إلى أداة التقييم الذاتي الشامل.';

const downloadTemplate = (templateId, kind) => {
  const url = `${API_URL}/api/templates/${templateId}/${kind}`;
  // Open in a new tab. Browser handles Content-Disposition: attachment to trigger
  // a real download while still working on iOS/Android.
  window.open(url, '_blank', 'noopener');
};

const TemplatesSection = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pendingKind, setPendingKind] = useState(null);
  const [recommendationData, setRecommendationData] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredTemplates =
    activeCategory === 'all'
      ? templates
      : templates.filter((t) => t.category === activeCategory);

  const handleDownloadClick = (template, kind) => {
    const recommendation = recommendationMessages[template.id];
    if (recommendation && template.recommendBefore?.length) {
      setSelectedTemplate(template);
      setPendingKind(kind);
      setRecommendationData(recommendation);
      setShowModal(true);
    } else {
      downloadTemplate(template.id, kind);
    }
  };

  const continueAnyway = () => {
    if (selectedTemplate && pendingKind) {
      downloadTemplate(selectedTemplate.id, pendingKind);
    }
    setShowModal(false);
  };

  const downloadRecommendedFirst = (templateId) => {
    // Default to PDF for the prerequisite suggestion
    downloadTemplate(templateId, 'pdf');
    setShowModal(false);
  };

  return (
    <section data-testid="templates-section" className="py-20 bg-[#F7F5F2]" dir="rtl">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-5 py-2 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-sm mb-5 tracking-wide">
            مكتبة الموارد المهنية
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic'] tracking-tight">
            الموارد المهنية الجاهزة
          </h2>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-9 mb-2 text-base sm:text-lg">
            مكتبة من القوالب والقوائم المرجعية المهنية الجاهزة للاستخدام داخل
            مؤسسات الطفولة المبكرة — موزّعة على ست فئات وفق المجال التشغيلي.
          </p>
          <p className="text-[#4A641F] text-xs font-medium mt-3">
            جميع الموارد محمية بحقوق الملكية الفكرية ولا يُسمح بإعادة البيع أو النشر دون إذن خطي.
          </p>
        </motion.div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
          {CATEGORIES.map((cat) => {
            const count =
              cat.id === 'all'
                ? templates.length
                : templates.filter((t) => t.category === cat.id).length;
            const active = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                data-testid={`resource-category-${cat.id}`}
                onClick={() => setActiveCategory(cat.id)}
                className={`text-xs sm:text-sm px-4 sm:px-5 py-2 rounded-full border transition-all ${
                  active
                    ? 'bg-[#1F3D2B] text-white border-[#1F3D2B] shadow-sm'
                    : 'bg-white text-[#3D512D] border-[#E6E0D6] hover:border-[#4A641F] hover:bg-[#F5F2EC]'
                }`}
              >
                {cat.label}
                <span className={`ms-2 text-[10px] ${active ? 'text-[#D8E0C9]' : 'text-[#92A082]'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.length === 0 ? (
            <div className="col-span-full text-center py-16 text-[#7A866E] bg-white rounded-2xl border border-dashed border-[#E6E0D6]">
              <p className="text-sm mb-1">قوالب هذه الفئة قيد التجهيز</p>
              <p className="text-xs text-[#92A082]">سيتم إضافتها قريباً ضمن مكتبة الموارد المهنية.</p>
            </div>
          ) : (
            filteredTemplates.map((template, index) => (
            <motion.div
              key={template.id}
              data-testid={`template-card-${template.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08, duration: 0.5 }}
              className="bg-white rounded-[20px] shadow-sm border border-[#E6E0D6] overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col"
            >
              {/* Image with Category Badge */}
              <div className="relative">
                <img
                  src={template.image}
                  alt={template.title}
                  className="w-full h-40 object-cover"
                />
                <span className="absolute top-3 right-3 bg-[#4A641F] text-white text-xs font-bold px-3 py-1 rounded-full">
                  {template.categoryLabel}
                </span>
              </div>

              {/* Content */}
              <div className="p-5 text-right flex-1 flex flex-col">
                <h3 className="font-bold text-lg text-[#1F3D2B] mb-1 font-['IBM_Plex_Sans_Arabic']">
                  {template.title}
                </h3>
                <span className="text-xs text-[#92A082] block mb-3">{template.titleEn}</span>
                <p className="text-sm text-[#6B7565] leading-7 mb-4">
                  {template.description}
                </p>

                {/* Free download — section header */}
                <div className="bg-[#F5F2EC] rounded-xl p-3.5 mb-3">
                  <p className="text-xs font-bold text-[#1F3D2B] mb-2.5 flex items-center gap-1.5">
                    <DownloadSimple size={14} weight="bold" className="text-[#4A641F]" />
                    تحميل النسخة المبسطة
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      data-testid={`download-${template.id}-word`}
                      onClick={() => handleDownloadClick(template, 'docx')}
                      className="flex items-center justify-center gap-1.5 bg-white hover:bg-[#1F3D2B] hover:text-white text-[#1F3D2B] border border-[#D8D2C8] py-2 rounded-lg text-sm font-medium transition-all duration-200"
                    >
                      <FileDoc size={16} weight="duotone" />
                      تحميل Word
                    </button>
                    <button
                      data-testid={`download-${template.id}-pdf`}
                      onClick={() => handleDownloadClick(template, 'pdf')}
                      className="flex items-center justify-center gap-1.5 bg-white hover:bg-[#1F3D2B] hover:text-white text-[#1F3D2B] border border-[#D8D2C8] py-2 rounded-lg text-sm font-medium transition-all duration-200"
                    >
                      <FilePdf size={16} weight="duotone" />
                      تحميل PDF
                    </button>
                  </div>
                </div>

                {/* Disclosure note */}
                <p className="text-xs text-[#7A866E] leading-6 mb-3 px-1">
                  {SIMPLIFIED_NOTE}
                </p>

                {/* Access CTA — direct sales suspended; tool available on request */}
                <div className="mt-auto pt-3 border-t border-[#E6E0D6]">
                  <p className="text-xs text-[#3D512D] leading-6 mb-2.5">
                    {FULL_TOOL_CTA}
                  </p>
                  <Link
                    to={template.id === 'diagnosis' ? '/contact' : `/tools/${template.id}/coming-soon`}
                    data-testid={`full-tool-cta-${template.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-[#1F3D2B] hover:bg-[#163020] text-white text-sm font-medium py-2.5 rounded-xl transition-all duration-200"
                  >
                    <Sparkle size={16} weight="fill" />
                    {template.id === 'diagnosis' ? 'طلب الأداة الكاملة' : 'الانتقال إلى الأداة الكاملة'}
                    <ArrowLeft size={14} className="rtl-flip" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )))}
        </div>

        {/* Footer Note */}
        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-sm text-[#7A866E]">
            جميع النماذج مصممة لتتوافق مع معايير الجودة الوطنية والدولية
          </p>
        </motion.div>
      </div>

      {/* Smart Recommendation Modal */}
      <AnimatePresence>
        {showModal && selectedTemplate && recommendationData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-[24px] max-w-md w-full p-6 shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
              dir="rtl"
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 left-4 text-[#7A866E] hover:text-[#1F3D2B] transition-colors"
              >
                <X size={24} />
              </button>

              <div className="w-14 h-14 rounded-2xl bg-[#F5F2EC] flex items-center justify-center mb-5">
                <Lightbulb size={28} weight="duotone" className="text-[#4A641F]" />
              </div>

              <h3 className="text-xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
                {recommendationData.title}
              </h3>
              <p className="text-[#6B7565] leading-8 mb-6">
                {recommendationData.message}
              </p>

              <div className="space-y-3 mb-4">
                {selectedTemplate.recommendBefore?.map((prereqId) => {
                  const prereq = templates.find((t) => t.id === prereqId);
                  if (!prereq) return null;
                  return (
                    <button
                      key={prereqId}
                      data-testid={`recommend-download-${prereqId}`}
                      onClick={() => downloadRecommendedFirst(prereqId)}
                      className="w-full bg-[#4A641F] hover:bg-[#3d5319] text-white py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <DownloadSimple size={18} />
                      تحميل {prereq.title} أولاً
                    </button>
                  );
                })}
              </div>

              <button
                data-testid="continue-download-btn"
                onClick={continueAnyway}
                className="w-full border-2 border-[#D8D2C8] text-[#1F3D2B] py-3 rounded-xl font-medium hover:bg-[#F5F2EC] transition-all duration-300 flex items-center justify-center gap-2"
              >
                متابعة إلى {selectedTemplate.title}
                <ArrowLeft size={18} className="rtl-flip" />
              </button>

              <p className="text-xs text-[#92A082] text-center mt-4">
                هذا اقتراح فقط، يمكنك المتابعة في أي وقت
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TemplatesSection;
